import type {
  GenUiKey,
  NextServiceId,
  NextServicesBlockMap,
  NextServicesProduct,
} from '@voll-intelligence/genui-contract';
import {
  composeNextServiceAsk,
  type NextServiceAnchor,
  type NextServiceAskRules,
  type NextServiceTranslate,
} from './core/ask.js';
import { BUS_ASK_RULES, busAnchor } from './products/bus.js';
import { FLIGHT_ASK_RULES, flightAnchor } from './products/flight.js';
import { HOTEL_ASK_RULES, hotelAnchor } from './products/hotel.js';
import { mobilityAnchor, rentalAnchor, STAY_ASK_RULES } from './products/stay.js';

/**
 * O que cada produto declara para o card, sem React: o mesmo componente de
 * linhas serve todos, e o produto dita só onde a pessoa fica (`anchor`), como
 * cada linha compõe o pedido (`askRules`) e a nota do rodapé.
 *
 * Métodos, não propriedades-função, de propósito: o parâmetro bivariante deixa
 * o contrato de um produto caber no tipo do registro inteiro.
 */
interface NextServicesProductContract<P extends NextServicesProduct> {
  readonly note: GenUiKey;
  readonly askRules: NextServiceAskRules;
  anchor(block: NextServicesBlockMap[P], locale: string): NextServiceAnchor;
}

const NOTE: GenUiKey = 'nextServices.note';

const NEXT_SERVICES_CONTRACTS: {
  readonly [P in NextServicesProduct]: NextServicesProductContract<P>;
} = {
  flight: { note: NOTE, askRules: FLIGHT_ASK_RULES, anchor: flightAnchor },
  hotel: { note: NOTE, askRules: HOTEL_ASK_RULES, anchor: hotelAnchor },
  bus: { note: 'bus.nextServices.note', askRules: BUS_ASK_RULES, anchor: busAnchor },
  rental: { note: NOTE, askRules: STAY_ASK_RULES, anchor: rentalAnchor },
  mobility: { note: NOTE, askRules: STAY_ASK_RULES, anchor: mobilityAnchor },
};

/** A nota do rodapé do card do produto. */
export function nextServicesNote(product: NextServicesProduct): GenUiKey {
  return NEXT_SERVICES_CONTRACTS[product].note;
}

/**
 * O pedido visível que cada linha manda na PRÓPRIA conversa (ação `reply`) —
 * composto só do payload do turno (anti-N+1), ancorado no que o produto sabe.
 */
export function nextServiceAsk<P extends NextServicesProduct>(
  product: P,
  service: NextServiceId,
  block: NextServicesBlockMap[P],
  t: NextServiceTranslate,
  locale: string,
): string {
  const contract = NEXT_SERVICES_CONTRACTS[product];
  return composeNextServiceAsk(contract.askRules[service], () => contract.anchor(block, locale), t);
}
